/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, message, Select, Table, TableProps, Tag, Tooltip, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { getUserInfo } from '../../reducers/User/actions';

const { Title } = Typography;

interface AccessRequest {
  key: number;
  request_id: number | string;
  email: string;
  organizations: string[];
  status: string;
  description: string;
  reviewed_by: string;
  requested_on: string;
  updated_on: string;
}

interface AccessRequestTableProps {
  refreshKey: number;
  onActionComplete?: () => void;
  onPendingOrgsChange?: (orgs: string[]) => void;
}

const AccessRequestTable: React.FC<AccessRequestTableProps> = ({
  refreshKey,
  onActionComplete,
  onPendingOrgsChange,
}) => {
  const userInfo: any = useSelector(getUserInfo);
  const isAdmin = userInfo?.role === 'Admin';
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [actionLoading, setActionLoading] = useState<Record<number, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchAccessRequests = async () => {
    setLoading(true);
    try {
      const response = await api.post('/auth/access-request/list', {
        page: currentPage,
        page_size: pageSize,
        search: searchValue,
        status: statusFilter,
      });
      const data: AccessRequest[] = (response?.access_requests || []).map((item: any, index: number) => ({
        key: index,
        request_id: item.request_id,
        email: item.email,
        organizations: item.organizations || [],
        status: item.status || 'pending',
        description: item.description || '',
        reviewed_by: item.reviewed_by || '-',
        requested_on: item.requested_on || '-',
        updated_on: item.updated_on || '-',
        total_count: response?.total_records ?? 0,
      }));
      setRequests(data);
      setTotalCount(response?.total_records ?? 0);
      const pendingOrgs = data.filter((r) => r.status?.toLowerCase() === 'pending').flatMap((r) => r.organizations);
      onPendingOrgsChange?.([...new Set(pendingOrgs)]);
    } catch {
      // Silently fail if endpoint doesn't exist yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccessRequests();
  }, [refreshKey, currentPage, pageSize, searchValue, statusFilter]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trimStart().toLowerCase();
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) {
      setSearchValue('');
      setCurrentPage(1);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setSearchValue(value.trim());
      setCurrentPage(1);
    }, 1000);
  };

  const handleSearchEnter = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchValue(inputValue.trim());
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination: any) => {
    const { current, pageSize: newPageSize } = pagination;
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    } else {
      setCurrentPage(current);
    }
  };

  const getStatusTag = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
    };
    return <Tag color={colorMap[status?.toLowerCase()] || 'default'}>{status}</Tag>;
  };

  const handleAction = async (record: AccessRequest, action: 'approved' | 'rejected') => {
    setActionLoading((prev) => ({ ...prev, [record.key]: action }));
    try {
      await api.post('/auth/access-request/update', {
        request_id: record.request_id,
        email: record.email,
        organization_ids: record.organizations,
        status: action === 'approved' ? 'approved' : 'rejected',
        reviewed_by: userInfo?.name,
      });
      message.success(`Request ${action} successfully.`);
      fetchAccessRequests();
      onActionComplete?.();
    } catch (error: any) {
      message.error(
        typeof error === 'string' ? error : `Failed to ${action === 'approved' ? 'approve' : 'reject'} request.`,
      );
    } finally {
      setActionLoading((prev) => {
        const updated = { ...prev };
        delete updated[record.key];
        return updated;
      });
    }
  };

  const columns: TableProps<AccessRequest>['columns'] = [
    {
      title: 'User',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Organizations',
      dataIndex: 'organizations',
      key: 'organizations',
      render: (orgs: string[]) => (
        <Flex gap={4} wrap="wrap">
          {orgs.map((name) => (
            <Tag key={name} color="blue">
              {name}
            </Tag>
          ))}
        </Flex>
      ),
    },
    ...(!isAdmin
      ? [
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => getStatusTag(status),
          },
        ]
      : []),
    {
      title: 'Requested On',
      dataIndex: 'requested_on',
      key: 'requested_on',
      render: (date: string, record: AccessRequest) => (
        <Flex gap={6} align="center">
          <span>{date}</span>
          {record.description && (
            <Tooltip title={record.description}>
              <InfoCircleOutlined style={{ color: '#1677ff', cursor: 'pointer' }} />
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: 'Reviewed By',
      dataIndex: 'reviewed_by',
      key: 'reviewed_by',
    },
    {
      title: 'Reviewed On',
      dataIndex: 'updated_on',
      key: 'updated_on',
    },
    ...(isAdmin
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 200,
            render: (_: any, record: AccessRequest) => {
              if (record.status?.toLowerCase() !== 'pending') {
                return <Tag color={record.status?.toLowerCase() === 'approved' ? 'green' : 'red'}>{record.status}</Tag>;
              }
              return (
                <Flex gap={8}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    loading={actionLoading[record.key] === 'approved'}
                    onClick={() => handleAction(record, 'approved')}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    loading={actionLoading[record.key] === 'rejected'}
                    onClick={() => handleAction(record, 'rejected')}
                  >
                    Reject
                  </Button>
                </Flex>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center">
        <Title level={4} style={{ margin: 0 }}>
          Request Access
        </Title>
        <Flex gap={8}>
          <Select
            style={{ width: 140 }}
            value={statusFilter}
            onChange={handleStatusFilterChange}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Pending', value: 'pending' },
              { label: 'Approved', value: 'approved' },
              { label: 'Rejected', value: 'rejected' },
            ]}
          />
          <Input
            style={{ width: 240 }}
            placeholder="Search by email"
            allowClear
            value={inputValue}
            onChange={handleSearchChange}
            onPressEnter={handleSearchEnter}
            suffix={<SearchOutlined />}
          />
        </Flex>
      </Flex>
      <Table
        scroll={{ x: 'max-content' }}
        loading={loading}
        columns={columns}
        dataSource={requests}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalCount,
          showSizeChanger: true,
          hideOnSinglePage: false,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
        }}
      />
    </Flex>
  );
};

export default AccessRequestTable;
